/**
 * Predictions from game performance. Each team gets an attack and a defence
 * rating from goals scored / conceded per game in completed (non-forfeit)
 * games, shrunk toward the league average so one big result doesn't dominate.
 * Expected goals for A vs B = leagueAvg × attack(A) × defence(B); scores are
 * Poisson. The rest of the season is simulated from the scheduled fixtures.
 */
import { buildMatchdays, compareRows, countsForStandings, fixtureStatus, latestReportableMatchday } from "./insights";

/** Games of league-average form added to every team's record. */
export const SHRINK_GAMES = 2;
const MAX_GOALS = 10;

const num = (v) => (v === null || v === undefined || v === "" ? null : Number(v));

export function teamRatings(fixtures, teamIds, mdByFixtureId, uptoMatchday) {
  const rec = new Map(teamIds.map((id) => [id, { gf: 0, ga: 0, n: 0 }]));
  let goals = 0;
  let sides = 0;
  for (const f of fixtures || []) {
    const md = mdByFixtureId[f.id];
    if (!md || md > uptoMatchday || fixtureStatus(f) !== "completed") continue;
    const hs = num(f.home_team_score);
    const as = num(f.away_team_score);
    [
      [f.home_team_id, hs, as],
      [f.away_team_id, as, hs],
    ].forEach(([id, gf, ga]) => {
      if (!rec.has(id)) rec.set(id, { gf: 0, ga: 0, n: 0 });
      const r = rec.get(id);
      r.gf += gf;
      r.ga += ga;
      r.n += 1;
    });
    goals += hs + as;
    sides += 2;
  }
  if (sides === 0 || goals === 0) return null;
  const avg = goals / sides;
  const ratings = {};
  for (const [id, r] of rec) {
    ratings[id] = {
      attack: (r.gf + SHRINK_GAMES * avg) / (r.n + SHRINK_GAMES) / avg,
      defence: (r.ga + SHRINK_GAMES * avg) / (r.n + SHRINK_GAMES) / avg,
      games: r.n,
    };
  }
  return { avg, ratings };
}

export function expectedGoals(model, homeId, awayId) {
  const h = model.ratings[homeId] || { attack: 1, defence: 1 };
  const a = model.ratings[awayId] || { attack: 1, defence: 1 };
  return { home: model.avg * h.attack * a.defence, away: model.avg * a.attack * h.defence };
}

function poissonPmf(lambda) {
  const p = [Math.exp(-lambda)];
  for (let k = 1; k <= MAX_GOALS; k += 1) p.push((p[k - 1] * lambda) / k);
  return p;
}

/** Win / draw / loss probabilities and the most likely score. */
export function matchProbabilities(lambdaHome, lambdaAway) {
  const ph = poissonPmf(lambdaHome);
  const pa = poissonPmf(lambdaAway);
  let home = 0;
  let draw = 0;
  let away = 0;
  let best = { p: -1, home: 0, away: 0 };
  for (let i = 0; i <= MAX_GOALS; i += 1) {
    for (let j = 0; j <= MAX_GOALS; j += 1) {
      const p = ph[i] * pa[j];
      if (i > j) home += p;
      else if (i < j) away += p;
      else draw += p;
      if (p > best.p) best = { p, home: i, away: j };
    }
  }
  const total = home + draw + away;
  return { home: home / total, draw: draw / total, away: away / total, likelyScore: [best.home, best.away] };
}

/** Deterministic PRNG so the same data always gives the same projections. */
export function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function samplePoisson(lambda, rand) {
  const L = Math.exp(-lambda);
  let k = 0;
  let p = 1;
  do {
    k += 1;
    p *= rand();
  } while (p > L && k < 30);
  return k - 1;
}

/**
 * @param standings rows as of the selected matchday (from standingsAtMatchday)
 * @param remaining fixtures still to be played
 */
export function simulateSeason({ standings, remaining, model, sims = 5000, seed = 12345 }) {
  const rand = mulberry32(seed);
  const ids = standings.map((r) => r.teamId);
  const tally = Object.fromEntries(
    ids.map((id) => [id, { positions: new Array(ids.length).fill(0), points: 0 }])
  );
  const xg = remaining.map((f) => ({ f, ...expectedGoals(model, f.home_team_id, f.away_team_id) }));

  for (let s = 0; s < sims; s += 1) {
    const rows = new Map(
      standings.map((r) => [
        r.teamId,
        { teamId: r.teamId, name: r.name, points: r.points, goalDifference: r.goalDifference, goalsFor: r.goalsFor, tiebreak: rand() },
      ])
    );
    for (const { f, home, away } of xg) {
      const h = rows.get(f.home_team_id);
      const a = rows.get(f.away_team_id);
      if (!h || !a) continue;
      const hs = samplePoisson(home, rand);
      const as = samplePoisson(away, rand);
      h.goalsFor += hs;
      a.goalsFor += as;
      h.goalDifference += hs - as;
      a.goalDifference += as - hs;
      if (hs > as) h.points += 3;
      else if (hs < as) a.points += 3;
      else {
        h.points += 1;
        a.points += 1;
      }
    }
    const table = [...rows.values()].sort((x, y) => compareRows(x, y) || x.tiebreak - y.tiebreak);
    table.forEach((r, i) => {
      tally[r.teamId].positions[i] += 1;
      tally[r.teamId].points += r.points;
    });
  }

  return standings
    .map((r) => {
      const t = tally[r.teamId];
      return {
        teamId: r.teamId,
        name: r.name,
        logoUrl: r.logoUrl,
        points: r.points,
        projectedPoints: t.points / sims,
        title: t.positions[0] / sims,
        top3: t.positions.slice(0, 3).reduce((a, b) => a + b, 0) / sims,
        positions: t.positions.map((n) => n / sims),
      };
    })
    .sort((a, b) => b.projectedPoints - a.projectedPoints || b.title - a.title || a.name.localeCompare(b.name));
}

/**
 * Predictions as of `matchday`: odds for the next matchday's games and a
 * projected final table. Returns null until there is at least one completed game with goals.
 */
export function buildPredictions({ fixtures, standings, mdByFixtureId, matchday, matchdays, sims }) {
  const teamIds = standings.map((r) => r.teamId);
  const model = teamRatings(fixtures, teamIds, mdByFixtureId, matchday);
  if (!model) return null;

  const isRemaining = (f) => {
    const md = mdByFixtureId[f.id];
    if (!md || f.home_team_id == null || f.away_team_id == null) return false;
    return md > matchday || !countsForStandings(f);
  };
  const remaining = (fixtures || []).filter(isRemaining);

  const nextMd = matchdays.find((m) => m.matchday > matchday && m.fixtures.length > 0) || null;
  const nextGames = nextMd
    ? nextMd.fixtures
        .filter((f) => f.home_team_id != null && f.away_team_id != null)
        .map((f) => {
          const xg = expectedGoals(model, f.home_team_id, f.away_team_id);
          return { fixtureId: f.id, homeTeamId: f.home_team_id, awayTeamId: f.away_team_id, xg, ...matchProbabilities(xg.home, xg.away) };
        })
    : [];

  return {
    model,
    nextMatchday: nextMd ? { matchday: nextMd.matchday, date: nextMd.date } : null,
    nextGames,
    remainingCount: remaining.length,
    table: remaining.length ? simulateSeason({ standings, remaining, model, sims }) : null,
  };
}

/**
 * The prediction Insights showed for each fixture before its matchday (win /
 * draw / loss odds and the most likely score), i.e. rated on results up to the
 * previous matchday. Games not yet played are only predicted on the next
 * matchday, like Insights. Fixtures on matchday 1 (or before any goals) have no
 * prediction and are left out.
 */
export function predictionsBefore(fixtures, fixtureIds) {
  const { matchdays, mdByFixtureId } = buildMatchdays(fixtures);
  const latest = latestReportableMatchday(matchdays);
  const nextMd = matchdays.find((m) => m.matchday > latest && m.fixtures.length > 0)?.matchday;
  const teamIds = [...new Set((fixtures || []).flatMap((f) => [f.home_team_id, f.away_team_id]).filter((x) => x != null))];
  const byId = new Map((fixtures || []).map((f) => [f.id, f]));
  const models = new Map();
  const out = {};
  for (const id of fixtureIds) {
    const f = byId.get(id);
    const md = mdByFixtureId[id];
    if (!f || !md || f.home_team_id == null || f.away_team_id == null) continue;
    if (!countsForStandings(f) && md !== nextMd) continue;
    if (!models.has(md)) models.set(md, teamRatings(fixtures, teamIds, mdByFixtureId, md - 1));
    const model = models.get(md);
    if (!model) continue;
    const xg = expectedGoals(model, f.home_team_id, f.away_team_id);
    out[id] = matchProbabilities(xg.home, xg.away);
  }
  return out;
}

/** The most likely score from `predictionsBefore` for each fixture. */
export function predictedScoresBefore(fixtures, fixtureIds) {
  const out = {};
  for (const [id, p] of Object.entries(predictionsBefore(fixtures, fixtureIds))) out[id] = p.likelyScore;
  return out;
}

/**
 * Points after the next matchday if every game ends with its most likely score.
 * Teams without a game keep their points. Returns null when there is nothing to forecast.
 */
export function forecastNextMatchday(standings, predictions) {
  if (!predictions?.nextMatchday || predictions.nextGames.length === 0) return null;
  const points = Object.fromEntries(standings.map((r) => [r.teamId, r.points]));
  for (const g of predictions.nextGames) {
    const [h, a] = g.likelyScore;
    if (h > a) points[g.homeTeamId] = (points[g.homeTeamId] || 0) + 3;
    else if (a > h) points[g.awayTeamId] = (points[g.awayTeamId] || 0) + 3;
    else {
      points[g.homeTeamId] = (points[g.homeTeamId] || 0) + 1;
      points[g.awayTeamId] = (points[g.awayTeamId] || 0) + 1;
    }
  }
  return { matchday: predictions.nextMatchday.matchday, date: predictions.nextMatchday.date, points };
}
